import { useEffect, useRef, useState } from "react";
import { createDecartClient, models } from "@decartai/sdk";
import "../Components/Styles/camera.scss";
import { decartToken } from "../Services/ApiServices/chatServices";

export default function CameraRecorderDecart() {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const processedStreamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const decartClientRef = useRef(null);
  const decartRealtimeRef = useRef(null);

  const [isDecartReady, setIsDecartReady] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [cameraFacing, setCameraFacing] = useState("user");
  const [error, setError] = useState(null);

  /* ===============================
     INIT DECART (CLIENT TOKEN)
     =============================== */
  useEffect(() => {
    const initDecart = () => {
      decartToken((response) => { // for env variables 
        if (response?.data?.client_token) {
          decartClientRef.current = createDecartClient({
            apiKey: response.data.client_token,
          });
        } else {
          console.log("Token missing:");
        }
        }, (err) => {
          console.log('Error occured in fetching token:', err, err.message || err.error);
        });
    };
    // initDecart();
  }, []);

  const isSecureContext = () => {
    if (typeof window === 'undefined') return false;
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const isHttps = window.location.protocol === 'https:';
    return isLocalhost || isHttps;
  };

  const getSecurityErrorMessage = () => {
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (isLocalhost) return null;
    return 'Camera access requires HTTPS. The application is being served over an insecure connection.';
  };

  const translateMediaError = (err) => {
    console.error("Camera error:", err);

    const name = err?.name;
    const message = err?.message || '';

    // iOS/WebView commonly surfaces insecure origin as TypeError
    if (name === 'TypeError' && message?.toLowerCase().includes('insecure')) {
      return 'Camera access blocked: serve the app over HTTPS (or localhost).';
    }

    if (name === 'NotReadableError') {
      return 'Could not start video source. The camera may be busy, blocked, or denied by another app. Close other apps and try again.';
    }

    if (name === 'NotAllowedError') {
      return 'Camera/microphone access denied. Please allow permissions in your browser Settings.';
    }

    if (name === 'NotFoundError') {
      return 'Requested device not found. No camera was detected. Check device connections and permissions.';
    }

    if (name === 'OverconstrainedError') {
      return 'Requested camera constraints are not supported on this device. Trying a safer fallback configuration.';
    }

    if (message) return message;
    return 'Failed to start camera.';
  };

  const getSupportedMediaRecorderMimeType = () => {
    const candidates = [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm;codecs=vp8',
      'video/webm',
    ];

    if (typeof window === 'undefined' || typeof window.MediaRecorder === 'undefined') return undefined;

    for (const mime of candidates) {
      try {
        if (MediaRecorder.isTypeSupported(mime)) return mime;
      } catch (_) {
        // ignore
      }
    }

    return undefined;
  };

  const debugEnumerateDevices = async () => {
    try {
      if (!navigator?.mediaDevices?.enumerateDevices) return;
      const devices = await navigator.mediaDevices.enumerateDevices();
      const summary = (devices || [])
        .map((d) => `${d.kind}:${d.deviceId}${d.label ? `:${d.label}` : ''}`)
        .slice(0, 20)
        .join(' | ');

      // Keep quiet unless we later display it via error.
      return summary;
    } catch (e) {
      return null;
    }
  };

  const handleCameraInitFailure = async (err) => {
    const debugDevices = await debugEnumerateDevices();
    const baseMessage = translateMediaError(err);

    const extra = debugDevices ? `\n\nDevice debug: ${debugDevices}` : '';
    setError(`${baseMessage}${extra}`);
    setIsDecartReady(false);
  };


  /* ===============================
     START CAMERA + DECART
     =============================== */
  const startCamera = async () => {
    if (!isSecureContext()) {
      const securityError = getSecurityErrorMessage();
      if (securityError) {
        console.error('Security error: Camera cannot be accessed over insecure (HTTP) connection');
        setError(securityError);
      }
      return;
    }

    if (!decartClientRef.current) return;

    setError(null);
    setIsDecartReady(false);

    const model = models.realtime("lucy-latest");

    const stopTracks = (s) => {
      try {
        s?.getTracks?.()?.forEach((t) => t.stop());
      } catch (_) {
        // ignore
      }
    };

    const buildVideoConstraints = (tier) => {
      // tier: 'model' | 'safe'
      if (tier === 'safe') {
        // Relax constraints: avoids OverconstrainedError/NotFoundError due to incompatible width/height.
        return { facingMode: cameraFacing };
      }

      return {
        facingMode: cameraFacing,
        frameRate: model.fps,
        width: model.width,
        height: model.height,
      };
    };

    let stream;
    try {
      // Tier 1: audio + model constraints
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: buildVideoConstraints('model'),
          audio: true,
        });
      } catch (e1) {
        // Tier 2: video-only + model constraints
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: buildVideoConstraints('model'),
            audio: false,
          });
        } catch (e2) {
          // Tier 3: relax constraints + video-only
          stream = await navigator.mediaDevices.getUserMedia({
            video: buildVideoConstraints('safe'),
            audio: false,
          });
        }
      }
    } catch (err) {
      stopTracks(stream);
      await handleCameraInitFailure(err);
      return;
    }

    streamRef.current = stream;

    try {
      decartRealtimeRef.current =
        await decartClientRef.current.realtime.connect(stream, {
          model,
          initialState: {
            prompt: { text: "Natural cinematic video", enhance: true },
          },
          onRemoteStream: (editedStream) => {
            processedStreamRef.current = editedStream;
            if (videoRef.current) {
              videoRef.current.srcObject = editedStream;
              videoRef.current.play();
            }
            setIsDecartReady(true);
          },
          onError: (e) => {
            console.error("Decart error:", e);
            setError(e?.message || "Connection failed");
            setIsDecartReady(false);
          },
        });
    } catch (err) {
      stopTracks(stream);
      await handleCameraInitFailure(err);
    }
  };

  /* ===============================
     RECORDING
     =============================== */
  const startRecording = () => {
    if (!isDecartReady || !processedStreamRef.current) return;

    const mimeType = getSupportedMediaRecorderMimeType();
    const options = mimeType ? { mimeType } : undefined;

    const recorder = new MediaRecorder(processedStreamRef.current, options);


    mediaRecorderRef.current = recorder;
    const chunks = [];

    recorder.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    recorder.onstop = () => {
      const blob = new Blob(chunks, { type: "video/webm" });
      console.log("✅ Recorded video:", URL.createObjectURL(blob));
    };

    recorder.start();
    setIsRecording(true);
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setIsRecording(false);
  };

  /* ===============================
     CLEANUP
     =============================== */
  const stopCamera = () => {
    if (decartRealtimeRef.current) decartRealtimeRef.current.disconnect();
    if (streamRef.current)
      streamRef.current.getTracks().forEach((t) => t.stop());

    processedStreamRef.current = null;
    setIsDecartReady(false);
    setError(null);
  };

  const switchCamera = async () => {
    stopCamera();
    setCameraFacing((p) => (p === "user" ? "environment" : "user"));
    await startCamera();
  };

  return (
    <div className="camera-wrapper">
      <video ref={videoRef} autoPlay playsInline muted />

      <div className="controls">
        <button onClick={startCamera}>Open Camera</button>
        <button onClick={switchCamera}>Switch</button>

        <button
          disabled={!isDecartReady}
          onClick={isRecording ? stopRecording : startRecording}
        >
          {isRecording ? "Stop Recording" : "Start Recording"}
        </button>

        <button onClick={stopCamera}>Close</button>
      </div>

      {error && <p className="error">{error}</p>}
      {!isDecartReady && !error && <p className="loading">Initializing live AI video…</p>}

      {/* iOS/WebView fallback: let the user use the native camera capture UI if permissions/device access is restricted */}
      <div style={{ position: "absolute", top: 90, width: "100%", display: "flex", justifyContent: "center", padding: "0 16px" }}>
        <label style={{ display: "flex", gap: 10, alignItems: "center", color: "#fff", fontSize: 12, opacity: 0.85, background: "rgba(0,0,0,0.35)", padding: "8px 12px", borderRadius: 999, cursor: "pointer" }}>
          <span>Fallback capture</span>
          <input
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: "none" }}
            onChange={() => {
              // No-op: capture input exists to provide a browser-native camera fallback in restricted environments.
            }}
          />
        </label>
      </div>
    </div>
  );
}